import React, { useRef, useState } from "react";
import { Upload, FileCheck2, CheckCircle2 } from "lucide-react";
import { useApp } from "@/context/AppContext";

const MAX_FILE = 1024 * 1024; // 1MB

function Label({ text, required }) {
  if (!text) return null;
  return (
    <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">
      {text}
      {required && <span className="ml-0.5 text-[#E0A106]">*</span>}
    </label>
  );
}

function ErrorMsg({ show, msg }) {
  if (!show) return null;
  return <div className="mt-1 text-[12.5px] text-[#dc2626]">{msg || "हे क्षेत्र आवश्यक आहे."}</div>;
}

const baseInput =
  "w-full rounded-md border px-3 py-2 text-[14px] outline-none transition-colors focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/15";

export function TextField({
  label, name, type = "text", required, readOnly, placeholder, maxLength, uppercase,
  errors = {}, note,
}) {
  const { application, setField } = useApp();
  const value = (application?.data?.[name]) ?? "";
  const invalid = !!errors[name];
  return (
    <div>
      <Label text={label} required={required} />
      <input
        type={type}
        name={name}
        value={value}
        readOnly={readOnly}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => setField(name, uppercase ? e.target.value.toUpperCase() : e.target.value)}
        style={uppercase ? { textTransform: "uppercase" } : undefined}
        className={`${baseInput} ${
          invalid ? "border-[#dc2626]" : "border-slate-300"
        } ${readOnly ? "cursor-not-allowed bg-slate-100 text-slate-600" : "bg-white"}`}
      />
      {note && <div className="mt-1 text-[12px] text-slate-500">{note}</div>}
      <ErrorMsg show={invalid} msg={errors[name]} />
    </div>
  );
}

export function SelectField({ label, name, options = [], required, readOnly, errors = {} }) {
  const { application, setField } = useApp();
  const value = (application?.data?.[name]) ?? options[0] ?? "";
  const invalid = !!errors[name];
  return (
    <div>
      <Label text={label} required={required} />
      <select
        name={name}
        value={value}
        disabled={readOnly}
        onChange={(e) => setField(name, e.target.value)}
        className={`${baseInput} ${invalid ? "border-[#dc2626]" : "border-slate-300"} ${
          readOnly ? "cursor-not-allowed bg-slate-100" : "bg-white"
        }`}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <ErrorMsg show={invalid} msg={errors[name]} />
    </div>
  );
}

export function TextareaField({ label, name, rows = 3, required, errors = {} }) {
  const { application, setField } = useApp();
  const value = (application?.data?.[name]) ?? "";
  const invalid = !!errors[name];
  return (
    <div>
      <Label text={label} required={required} />
      <textarea
        name={name}
        rows={rows}
        value={value}
        onChange={(e) => setField(name, e.target.value)}
        className={`${baseInput} resize-y ${invalid ? "border-[#dc2626]" : "border-slate-300"} bg-white`}
      />
      <ErrorMsg show={invalid} msg={errors[name]} />
    </div>
  );
}

export function FileField({ label, name, required, note = "JPG/PNG/PDF, कमाल आकार 1MB", errors = {}, compact }) {
  const { application, uploadFile } = useApp();
  const stored = (application?.data?.[name]);
  const inputRef = useRef(null);
  const invalid = !!errors[name];
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const onChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploadErr("");
    if (file.size > MAX_FILE) {
      setUploadErr("फाईलचा आकार 1MB पेक्षा जास्त आहे. कृपया 1MB पेक्षा कमी फाईल निवडा.");
      e.target.value = "";
      return;
    }
    try {
      setUploading(true);
      await uploadFile(name, file);
    } catch (err) {
      setUploadErr(err.message || "अपलोड अयशस्वी.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <Label text={label} required={required} />
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 transition-colors hover:border-[#1b5e3f] ${
          invalid ? "border-[#dc2626]" : "border-slate-300"
        } ${stored ? "bg-emerald-50" : "bg-slate-50"}`}
      >
        <span className="flex items-center gap-1.5 whitespace-nowrap rounded bg-white px-2.5 py-1 text-[13px] font-medium text-slate-700 shadow-sm ring-1 ring-slate-200">
          <Upload className="h-3.5 w-3.5" /> Choose File
        </span>
        <span className="truncate text-[13px] text-slate-500">
          {uploading ? "अपलोड होत आहे..." : stored ? stored.name : "No file chosen"}
        </span>
      </div>
      <input ref={inputRef} type="file" name={name} accept=".jpg,.jpeg,.png,.pdf" onChange={onChange} className="hidden" />
      {!compact && <div className="mt-1 text-[12px] text-slate-500">{note}</div>}
      {stored && !uploading && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#3a6b1c]">
          <FileCheck2 className="h-4 w-4" /> अपलोड: <strong className="font-semibold">{stored.name}</strong>
        </div>
      )}
      {uploadErr && <div className="mt-1 text-[12.5px] text-[#dc2626]">{uploadErr}</div>}
      <ErrorMsg show={invalid} msg={errors[name]} />
    </div>
  );
}

// Yes / No radio pair
export function YesNo({ name, yesLabel = "आहे", noLabel = "नाही", onToggle, value }) {
  const { application, setField } = useApp();
  const current = value !== undefined ? value : (application?.data?.[name]);
  const choose = (v) => {
    setField(name, v);
    onToggle && onToggle(v);
  };
  const pill = (active) =>
    `flex items-center gap-2 rounded-full border px-4 py-1.5 text-[14px] cursor-pointer transition-all ${
      active ? "border-[#1b5e3f] bg-[#1b5e3f] text-white shadow-sm" : "border-slate-300 bg-white text-slate-700 hover:border-[#1b5e3f]"
    }`;
  return (
    <div className="flex flex-wrap gap-3">
      <button type="button" onClick={() => choose("yes")} className={pill(current === "yes")}>
        <span className={`h-3 w-3 rounded-full border-2 ${current === "yes" ? "border-white bg-white" : "border-slate-400"}`} />
        {yesLabel}
      </button>
      <button type="button" onClick={() => choose("no")} className={pill(current === "no")}>
        <span className={`h-3 w-3 rounded-full border-2 ${current === "no" ? "border-white bg-white" : "border-slate-400"}`} />
        {noLabel}
      </button>
    </div>
  );
}

export function Fieldset({ legend, children }) {
  return (
    <fieldset className="mb-5 rounded-xl border border-t-[3px] border-slate-200 border-t-[#1b5e3f] bg-white px-5 pb-5 pt-4 shadow-[0_2px_10px_rgba(27,94,63,0.06)]">
      {legend && <legend className="px-2 text-[15px] font-bold text-[#c2570a]">{legend}</legend>}
      {children}
    </fieldset>
  );
}

// Reusable numbered question card — used across all form stages for a
// consistent, clearly separated look (gap, shadow, accent strip, alternating bg, number badge)
export function QuestionCard({ index, number, title, required, note, headerRight, children, testid, indent = true }) {
  const badge = number ?? (index != null ? index + 1 : null);
  const alt = index != null && index % 2 === 1;
  return (
    <div
      data-testid={testid}
      className={`mb-5 overflow-hidden rounded-xl border border-t-[3px] border-slate-200 border-t-[#1b5e3f] shadow-[0_2px_10px_rgba(27,94,63,0.06)] transition-shadow hover:shadow-[0_5px_18px_rgba(27,94,63,0.12)] ${
        alt ? "bg-[#faf8f0]" : "bg-white"
      }`}
    >
      <div className="p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            {badge != null && (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1b5e3f] text-[15px] font-bold text-white shadow-sm">
                {badge}
              </span>
            )}
            <div className="pt-1 text-[15px] font-semibold text-[#1b5e3f]">
              {title}
              {required && <span className="ml-0.5 text-[#E0A106]">*</span>}
            </div>
          </div>
          {headerRight && <div className="shrink-0">{headerRight}</div>}
        </div>
        {note && <p className={`mb-3 -mt-1 text-[12.5px] leading-relaxed text-[#7a6a45] ${indent ? "sm:pl-11" : ""}`}>{note}</p>}
        <div className={indent ? "sm:pl-11" : ""}>{children}</div>
      </div>
    </div>
  );
}

export function Criteria({ children, label = "मूल्यांकनाचे निकष:" }) {
  return (
    <div className="mb-4 rounded-md border-l-[3px] border-[#1b5e3f] bg-slate-50 px-4 py-2.5 text-[14px]">
      <strong>{label}</strong> {children}
    </div>
  );
}

export { MAX_FILE };
